import { getCountries, getCountryCallingCode } from 'libphonenumber-js';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import dayjs from 'dayjs';

class Helpers {
  static downloadFile(dataURL, name) {
    if (name.indexOf('?') !== -1) {
      name = name.substring(0, name.indexOf('?'));
    }

    const link = document.createElement('a');

    link.href = dataURL;

    link.download = name;

    document.body.appendChild(link);

    link.click();

    link.remove();
  }

  downloadFileFromURL(fileURL, name) {
    fetch(fileURL).then((res) =>
      res

        .blob()

        .then((blob) => URL.createObjectURL(blob))

        .then((dataURL) => Helpers.downloadFile(dataURL, name))
    );
  }

  static formatTimeWithArabicIndicators(
    timeString: string,
    locale: string
  ): string {
    if (locale === 'ar') {
      return timeString
        .replace('AM', 'صباحا')
        .replace('PM', 'مساء')
        .replace('am', 'صباحا')
        .replace('pm', 'مساء');
    }
    return timeString;
  }
  static convertUTCToLocalTimeString(utcDateStr: string): string {
    const date = new Date(utcDateStr);

    return new Intl.DateTimeFormat(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  }
  static getTimeAgoFromUTC = (
    postdateNew: string | null | undefined,
    locale: string
  ) => {
    if (
      !postdateNew ||
      typeof postdateNew !== 'string' ||
      postdateNew.trim() === ''
    ) {
      return '';
    }
    try {
      const localDateStr = Helpers.convertUTCToUserLocal(postdateNew);
      if (!localDateStr) return '';
      const date = parseISO(localDateStr);
      if (isNaN(date.getTime())) return '';
      const timeAgo = formatDistanceToNow(date, {
        addSuffix: true,
        locale: locale === 'ar' ? arLocale : undefined,
      });
      return timeAgo;
    } catch {
      return '';
    }
  };
  static convertUTCToDateOnly(utcString?: string): string {
    if (!utcString) return '';

    // Parse as UTC and format as yyyy-MM-dd using date-fns
    // Note: parseISO treats input as UTC if it ends with 'Z'
    // format always outputs in local time, so use utcToZonedTime if needed
    // But for just the date part, parseISO + format is enough
    // If input does not end with 'Z', append it to ensure UTC parsing
    const date = parseISO(
      utcString.endsWith('Z') ? utcString : utcString + 'Z'
    );
    return format(date, 'yyyy-MM-dd');
  }
  static generateSize(fileSizeBytes: number) {
    if (fileSizeBytes >= 1024 * 1024) {
      return {
        size: parseFloat((fileSizeBytes / (1024 * 1024)).toFixed(2)),
        type: 'MB',
      };
    } else if (fileSizeBytes >= 1024) {
      return {
        size: parseFloat((fileSizeBytes / 1024).toFixed(2)),
        type: 'KB',
      };
    } else {
      return { size: fileSizeBytes, type: 'Byte' };
    }
  }
  static calculateFullBudget = (budgetPerDay: number, totalDays: number) => {
    return budgetPerDay * totalDays;
  };
  static convertUTCToUserLocal(utcString: Date) {
    const utcDate = new Date(utcString + 'Z'); // Make sure it's parsed as UTC

    const year = utcDate.getFullYear();
    const month = String(utcDate.getMonth() + 1).padStart(2, '0');
    const day = String(utcDate.getDate()).padStart(2, '0');

    // Now convert to local time
    const localDate = new Date(
      utcDate.toLocaleString('en-US', {
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
    );

    const localHours = String(localDate.getHours()).padStart(2, '0');
    const localMinutes = String(localDate.getMinutes()).padStart(2, '0');
    const localSeconds = String(localDate.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day}T${localHours}:${localMinutes}:${localSeconds}`;
  }

  static getCountryCodeFromCallingCode(callingCode: string) {
    // Remove the plus sign if present
    const cleanCallingCode = callingCode.replace(/^\+/, '');

    // Get all available countries
    const countries = getCountries();

    // Find the first country that matches the calling code
    const matchingCountry = countries.find((country) => {
      try {
        return getCountryCallingCode(country) === cleanCallingCode;
      } catch {
        return false;
      }
    });

    return matchingCountry || 'NO Code';
  }
  static async downloadImage(fileUrl: string, filename: string) {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'document';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  }

  static truncateText(text: string, maxLength: number): string {
    if (text?.length <= maxLength) return text;
    return text?.slice(0, maxLength) + '.....';
  }
  /**
   * Converts a timestamp string to a local time string in "h:mm am/pm" format.
   * @param timestamp - Date/time string
   * @returns Formatted time string (e.g., "3:45 pm")
   */
  static formatTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    return date
      .toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
      .toLowerCase();
  };

  static groupBy<T, K extends string | number>(
    array: T[],
    getKey: (item: T) => K
  ): Record<K, T[]> {
    return array.reduce(
      (acc, item) => {
        const key = getKey(item);
        (acc[key] ||= []).push(item);
        return acc;
      },
      {} as Record<K, T[]>
    );
  }

  static getCalendarDuration(start: string | Date, end: string | Date) {
    const startDate = dayjs(start);
    const endDate = dayjs(end);

    const months = endDate.diff(startDate, 'month');
    const adjustedStart = startDate.add(months, 'month');

    const days = endDate.diff(adjustedStart, 'day');
    const adjustedStartWithDays = adjustedStart.add(days, 'day');

    const hours = endDate.diff(adjustedStartWithDays, 'hour');
    const adjustedStartWithHours = adjustedStartWithDays.add(hours, 'hour');

    const minutes = endDate.diff(adjustedStartWithHours, 'minute');

    return { months, days, hours, minutes };
  }

  static downloadBlobFile({
    blob,
    fileName,
  }: {
    blob: Blob;
    fileName: string;
  }) {
    const url = window.URL.createObjectURL(blob);

    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  static formatMoney = (amount: number, locale = 'en-US') => {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };
}

export default Helpers;
